import { useRef, useState } from "react";
import { Upload, Loader2, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "../../lib/api";

export default function ImageField({
  value,
  onChange,
  onChangeMany,
  testId,
  accept = "image/*,video/mp4,video/webm,video/quicktime",
  multiple = false,
  showInput = true,
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const inputRef = useRef(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setProgress(files.length > 1 ? `0/${files.length}` : "");
    try {
      const urls = [];
      for (let index = 0; index < files.length; index += 1) {
        const fd = new FormData();
        fd.append("file", files[index]);
        const { data } = await api.post("/admin/upload", fd);
        urls.push(data.url);
        if (files.length > 1) setProgress(`${index + 1}/${files.length}`);
      }
      if (multiple) {
        onChangeMany?.(urls);
        if (!onChangeMany) onChange(urls.join("\n"));
      } else {
        onChange(urls[0]);
      }
      toast.success(files.length > 1 ? `${files.length} files uploaded` : "Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      setProgress("");
      e.target.value = "";
    }
  };

  const isVideo = value && (value.endsWith(".mp4") || value.endsWith(".webm") || value.endsWith(".mov"));
  const isPdf = value && value.endsWith(".pdf");

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {showInput && (
          <input
            data-testid={testId}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https:// file URL or upload"
            className="flex-1 min-w-0 bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-lg px-3 py-2 text-sm outline-none transition-colors"
          />
        )}
        <button
          type="button"
          data-testid={`${testId}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#EFEAE1] border border-[#9A7B66]/25 text-[#9A7B66] text-xs font-semibold hover:bg-[#9A7B66] hover:text-white transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? `Uploading ${progress}` : multiple ? "Upload Multiple" : "Upload"}
        </button>
        {value && (
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onClick={() => onChange("")}
            title="Hapus / Kosongkan"
            className="p-2 rounded-lg border border-[#E5DED4] text-[#A0988E] hover:text-[#C5534A] hover:border-[#C5534A]/40 bg-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <input ref={inputRef} type="file" hidden accept={accept} multiple={multiple} onChange={upload} />
      </div>
      {value && !isVideo && !isPdf && (
        <img
          data-testid={`${testId}-preview`}
          src={mediaUrl(value)}
          alt="preview"
          className="w-full max-w-md h-44 rounded-lg object-contain border border-[#E5DED4] bg-[#F4EFEA] shadow-sm"
        />
      )}
      {value && isVideo && (
        <span className="text-[10px] uppercase tracking-wider text-[#9A7B66] font-semibold">video</span>
      )}
      {value && isPdf && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#9A7B66] font-semibold">
          <FileText className="w-4 h-4" /> PDF
        </span>
      )}
    </div>
  );
}
